import { createBooleanFeatureFlag, createFeatureFlagConfig, createRemoteFeatureFlag } from './featureFlags.factories';

export const FEATURE_FLAG_VALUES = {
  layout: createFeatureFlagConfig('layout', ['aloha', 'brannan']),
  guestsitetarget: createFeatureFlagConfig('guestsitetarget', ['bliss', 'charm']),
  nossr: createBooleanFeatureFlag('nossr'),
  joyWeb: createFeatureFlagConfig('joy-web', true),
  pseudoLocalization: createFeatureFlagConfig('pseudoloc', ['true', 'all']),
  bookmarkletPopup: createBooleanFeatureFlag('bookmarkletPopup'),
  enableSsr: createBooleanFeatureFlag('enablessr'),
  doRegistryOnboardingCardsReset: createBooleanFeatureFlag('doRegistryOnboardingCardsReset'),
  registryEnableGuestNavRegistryLinks: createRemoteFeatureFlag({ key: 'registryEnableGuestNavRegistryLinks', supportedValues: ['on', 'off'] }),
  registryShowStandalonePageTitle: createRemoteFeatureFlag({ key: 'registryShowStandalonePageTitle', supportedValues: ['on', 'off'] }),
  registryOnboardingSurvey: createFeatureFlagConfig('registryOnboardingSurvey', ['force', 'reset', 'force5']),
  registryOnboardingV3Experiment: createRemoteFeatureFlag({
    key: 'registryOnboardingV3Experiment',
    supportedValues: ['control', 'treatment'],
  }),
  enableRegistryTransfer: createBooleanFeatureFlag('enableRegistryTransfer', true),
  enableHaveYouPurchasedModal: createBooleanFeatureFlag('enableHaveYouPurchasedModal', true),
  enableHaveYouPurchasedModalExternalLinkClick: createBooleanFeatureFlag('enableHaveYouPurchasedModalExternalLinkClick', true),
  enableCashFundCheckoutV2: createBooleanFeatureFlag('enableCashFundCheckoutV2', true),
  enableLoadingStyleApplicator: createBooleanFeatureFlag('enableLoadingStyleApplicator'),
  enableAddressVerificationModal: createBooleanFeatureFlag('enableAddressVerificationModal'),
  enableAddEditGiftNote: createBooleanFeatureFlag('enableAddEditGiftNote', true),
  enableCurationHomeExpert: createBooleanFeatureFlag('enableCurationHomeExpert', true),
  enableBuyForMyselfFromPurchaseConfirmation: createBooleanFeatureFlag('enableBuyForMyselfFromPurchaseConfirmation', true),
  enableRandomLabelsGuestRegistryProducts: createBooleanFeatureFlag('enableRandomLabelsGuestRegistryProducts'),
  enableSplitPurchaseDialog: createBooleanFeatureFlag('enableSplitPurchaseDialog', true),
  enableShippingAddressWarning: createBooleanFeatureFlag('enableShippingAddressWarning', true),
  orderTrackingFeature: createBooleanFeatureFlag('orderTrackingFeature', true),
  enableMFASettings: createBooleanFeatureFlag('enableMFASettings', true),
  enableAffiliateCheckoutV2: createBooleanFeatureFlag('enableAffiliateCheckoutV2', true),
  enableNewExternalRegistry: createBooleanFeatureFlag('enableNewExternalRegistry'),
  enableChecklistInGiftBasketWidget: createBooleanFeatureFlag('enableChecklistInGiftBasketWidget', true),
  enableBlankDates: createBooleanFeatureFlag('enableBlankDates', true),
  enableCustomEventName: createBooleanFeatureFlag('enableCustomEventName', true),
  enableBabyRegistry: createBooleanFeatureFlag('enableBabyRegistry', true),
  enableExternalRegistriesForBabyReg: createBooleanFeatureFlag('enableExternalRegistriesForBabyReg', true),
  enableBabyRegistrySoftLaunchFeatures: createBooleanFeatureFlag('enableBabyRegistrySoftLaunchFeatures', true),
  seamlessCheckoutVariant: createRemoteFeatureFlag({ key: 'wgseamlesscheckout', supportedValues: ['variant1', 'variant2', 'variant3'] }),
  joyRefundsEnabled: createRemoteFeatureFlag({ key: 'joyrefundsenabled', supportedValues: ['on', 'off'] }),
  guestLinkUseRedirectService: createRemoteFeatureFlag({ key: 'guestlinkuseredirectservice', supportedValues: ['on', 'off'] }),
  guestInAppBrowserBanner: createRemoteFeatureFlag({ key: 'guestinappbrowserbanner', supportedValues: ['on', 'off'], defaultValue: 'off' }),
  enableInlineEditing: createBooleanFeatureFlag('enableInlineEditing', false),
  isMobileInlineEditMode: createBooleanFeatureFlag('isMobileInlineEditMode', false),
  supportAutocompletePreDefinedPlacesEnabled: createRemoteFeatureFlag({ key: 'supportAutocompletePreDefinedPlacesEnabled', supportedValues: ['on', 'off'] }),
  websiteBannerMapping: createRemoteFeatureFlag('websitebannermapping'),
  rsvpEnableAccommodationsRedirect: createRemoteFeatureFlag({ key: 'rsvpenableaccommodationsredirect', supportedValues: ['control', 'treatment'] }),
  enableRegistryRedirectInMessaging: createRemoteFeatureFlag('enableRegistryRedirectInMessaging'),
  messageDraftsEnabled: createRemoteFeatureFlag({ key: 'messagedraftsenabled', supportedValues: ['on', 'off'] }),
  messageSchedulingEnabled: createRemoteFeatureFlag({ key: 'messageschedulingenabled', supportedValues: ['on', 'off'] }),
  newCashFundsEnabled: createRemoteFeatureFlag('newCashFundsEnabled'),
  websiteCashFundComponentEnabled: createRemoteFeatureFlag('websiteCashFundComponentEnabled'),
  websiteCashFundsi18nEnabled: createRemoteFeatureFlag('websiteCashFundsi18nEnabled'),
  // Gates the publish flow behind email verification. When 'on', publishing opens the
  // email verification dialog for unverified users; when 'off', publish proceeds as before.
  publishEmailVerificationEnabled: createRemoteFeatureFlag({ key: 'publishemailverificationenabled', supportedValues: ['on', 'off'], defaultValue: 'off' }),
  weddingGeneralMessagingEnabled: createRemoteFeatureFlag('weddingGeneralMessagingEnabled'),
  /** Compose-time intent detection in General Messaging — surfaces a Cash Funds nudge when the draft semantically asks for money. See NREG-3133 + `joy-web/.claude/plans/message-detector.md`. */
  messageIntentDetectionEnabled: createRemoteFeatureFlag('messageIntentDetectionEnabled'),
  // Default 'on' so the guest deadline query (useRsvpDeadlineGuard) fires immediately on
  // hydration instead of waiting on the Amplitude experiment resolution. Without a default
  // the flag evaluates to 'off' during load, the deadline query is skipped, and guests briefly
  // see the RSVP button with no deadline (LEGRSVP). The flag is fully rolled out, so the default
  // matches production; a remote 'off' override still takes effect once Amplitude resolves.
  rsvpDeadlineEnabled: createRemoteFeatureFlag({
    key: 'rsvpDeadlineEnabled',
    supportedValues: ['on', 'off'],
    defaultValue: 'on',
  }),
  /** Gates the RSVP admin "Activity" tab (read-only feed of RSVP responses + updates). MSG-14. */
  rsvpActivityTabEnabled: createRemoteFeatureFlag({
    key: 'rsvpActivityTabEnabled',
    supportedValues: ['on', 'off'],
  }),
  /** Gates the attendance-question "Events" control that links RSVP questions to Schedule-page events. */
  rsvpLinkedEventsEnabled: createRemoteFeatureFlag({
    key: 'rsvpLinkedEventsEnabled',
    supportedValues: ['on', 'off'],
  }),

  /**
   * Is being AB tested
   */
  // this is a long-lived flag; the Feature is only shown to UK Couples, a filter powered entirely by Amplitude
  cambiumGroupRegistryReferralsEnabled: createRemoteFeatureFlag('cambiumGroupRegistryReferralsEnabled'),
  universalCatalogSearchEnabled: createRemoteFeatureFlag('universalCatalogSearchEnabled'),
  universalCatalogMeshExperiment: createRemoteFeatureFlag({
    key: 'universalCatalogMeshExperiment',
    supportedValues: ['control', 'low-volume-google-shopping-first', 'low-volume-joy-catalog-first', 'joy-catalog-first'],
  }),
  websiteWeatherReportEnabled: createRemoteFeatureFlag('websiteWeatherReportEnabled'),
  registryCompactDonationViewEnabled: createRemoteFeatureFlag({
    key: 'registryCompactDonationViewEnabled',
    supportedValues: ['on', 'off'],
    defaultValue: 'off',
  }),

  /**
   * Experiment gating the guest wedding-code banner AND the weddingCode injection into travel
   * checkout. Gated in one place (useWeddingCodeBannerEligibility) so banner + checkout stay in
   * lockstep. Shown only for the 'treatment' variant; 'control' is the holdout. Default 'control'
   * so the frontend ships dark and the experiment is ramped via Amplitude.
   */
  weddingCodeBannerExperiment: createRemoteFeatureFlag({
    key: 'weddingCodeBannerExperiment',
    supportedValues: ['control', 'treatment'],
    defaultValue: 'control',
  }),

  /* things to do experiments */
  websiteThingsToDoLayoutExperiment: createRemoteFeatureFlag({ key: 'websiteThingsToDoLayoutExperiment', supportedValues: ['clean', 'modal', 'links', 'off'] }),
  honeymoonPlanningExperiment: createRemoteFeatureFlag({ key: 'honeymoonPlanningExperiment', supportedValues: ['on', 'off'] }),
  /** Kill switch for all cash fund partner placements. Remove a variant value to disable that partner globally across all placements. */
  activeCashFundPartner: createRemoteFeatureFlag({ key: 'activeCashFundPartner', supportedValues: ['ally', 'chase', 'revolut'] }),
  /** Placement: create-fund dialog (`CreateFundDialog`). Gated with activeCashFundPartner. */
  enableCashFundPartnerCreateFundDialog: createRemoteFeatureFlag({ key: 'enableCashFundPartnerCreateFundDialog', supportedValues: ['ally', 'chase'] }),
  /** Placement: registry admin gift tracker empty state (`AllyBanner`). Gated with activeCashFundPartner. */
  enableCashFundPartnerGiftTracker: createRemoteFeatureFlag({ key: 'enableCashFundPartnerGiftTracker', supportedValues: ['ally', 'chase'] }),
  /** Placement: Cash Funds shop nav dropdown (4th panel) + Cash Funds dept page (first carousel tile). Gated with activeCashFundPartner. */
  enableCashFundPartnerShop: createRemoteFeatureFlag({ key: 'enableCashFundPartnerShop', supportedValues: ['ally', 'chase'] }),
  /** Placement: transfer-to-bank dialog (`TransferToBankDialogView`). Gated with activeCashFundPartner. */
  enableCashFundPartnerTransferToBank: createRemoteFeatureFlag({ key: 'enableCashFundPartnerTransferToBank', supportedValues: ['ally', 'chase', 'revolut'] }),
  /** Placement: transfer-to-bank dialog repeat nudge shown when bank is already connected (pending/confirm state). Gated with activeCashFundPartner. */
  enableCashFundPartnerTransferToBankRepeat: createRemoteFeatureFlag({ key: 'enableCashFundPartnerTransferToBankRepeat', supportedValues: ['ally', 'chase', 'revolut'] }),
  /**
   * Prevent using cached SSR html.
   */
  skipSsrCache: createBooleanFeatureFlag('skipssrcache'),
  logSsrUserAgent: createBooleanFeatureFlag('logSsrUserAgent'),

  exampleOnOffRemoteFeatureFlag: createRemoteFeatureFlag('exampleOnOffFeatureFlag'),
  exampleMultiVariantRemoteFeatureFlag: createRemoteFeatureFlag({ key: 'examplemultivariantfeatureflag', supportedValues: ['variant1', 'variant2'], defaultValue: 'variant1' }),
  exampleExperimentFeatureFlag: createRemoteFeatureFlag({ key: 'exampleexperimentfeatureflag', supportedValues: ['control', 'treatment'] }),
  exampleExperimentBucketedByJoyEvent: createRemoteFeatureFlag({ key: 'exampleexperimentbucketedbyjoyevent', supportedValues: ['control', 'treatment'] }),
  adminDashboardOverviewFAQs: createRemoteFeatureFlag({ key: 'adminDashboardOverviewFAQs', supportedValues: ['control', 'treatment'] }),
  adminDashboardOverviewHighlights: createRemoteFeatureFlag({ key: 'adminDashboardOverviewHighlights', supportedValues: ['control', 'variant1', 'variant2'] }),
  /*
   * Gift Wrap Remote Feature Flags
   */
  registryGiftWrapDefaultUSABillingAddress: createRemoteFeatureFlag('registrygiftwrapdefaultusabillingaddress'),
  registryGiftWrapEcardPrice: createRemoteFeatureFlag('registrygiftwrapecardprice'),
  registryGiftWrapExperimentEnabled: createRemoteFeatureFlag('registrygiftwrapexperimentenabled'),
  registryGiftWrapCashEnabled: createRemoteFeatureFlag('registrygiftwrapcashenabled'),
  registryGiftWrapAffiliateEnabled: createRemoteFeatureFlag('registrygiftwrapaffiliateenabled'),
  registryGiftWrapThemeDisplayCount: createRemoteFeatureFlag('registrygiftwrapthemedisplaycount'),
  registryGiftWrapThemeExpandIcon: createRemoteFeatureFlag({
    key: 'registrygiftwrapthemeexpandicon',
    supportedValues: ['plus', 'chevron'],
    defaultValue: 'plus',
  }),
  rsvpCongratulationsEcardUserIsInUS: createRemoteFeatureFlag('rsvpCongratulationsEcardUserIsInUS'),
  rsvpCongratulationsEcardExperimentEnabled: createRemoteFeatureFlag('rsvpCongratulationsEcardExperimentEnabled'),
  rsvpCongratulationsECardPrice: createRemoteFeatureFlag('rsvpCongratulationsECardPrice'),
  rsvpCongratulationsEventHandleExcludeList: createRemoteFeatureFlag('rsvpcongratulationseventhandleexcludelist'),
  rsvpVersion: createRemoteFeatureFlag({ key: 'rsvpVersion', supportedValues: ['basic', 'offer'] }),
  rsvpGuestFlowAccommodationsStep: createRemoteFeatureFlag({ key: 'rsvpguestflowaccommodationsstep', supportedValues: ['control', 'treatment', 'off'] }),
  rsvpGuestFlowAccommodationsStepWithAccoms: createRemoteFeatureFlag({ key: 'rsvpguestflowaccommodationsstepwithaccoms', supportedValues: ['control', 'treatment'] }),
  enableRsvpReminderMessage: createRemoteFeatureFlag('enablersvpremindermessage'),
  rsvpButtonCustomLabel: createRemoteFeatureFlag('rsvpbuttoncustomlabel'),
  /*
   * Print Remote Feature Flags
   */
  premiumEcardMessagingV3: createRemoteFeatureFlag({ key: 'premiumecardmessagingv3', supportedValues: ['on', 'off'] }),
  premiumEcardMessagingV3HistoryEnabled: createRemoteFeatureFlag({ key: 'premiumecardmessagingv3historyenabled', supportedValues: ['on', 'off'] }),
  messagingCenterPollingRates: createRemoteFeatureFlag({ key: 'messagingcenterpollingrates', supportedValues: ['on', 'off'] }),
  askGuestForHotelRecommendationsEnabled: createRemoteFeatureFlag({ key: 'askguestforhotelrecommendationsenabled', supportedValues: ['on', 'off'] }),
  printCouponCodeLaunch: createRemoteFeatureFlag({ key: 'printcouponcodelaunch', supportedValues: ['on'] }),
  printCreateWeddingRedirect: createRemoteFeatureFlag({ key: 'printcreateweddingredirect', supportedValues: ['control', 'treatment'] }),
  printDesignConciergeCheckoutEnabled: createRemoteFeatureFlag('printdesignconciergecheckoutenabled'),
  printSuperAdminDiscountEnabled: createRemoteFeatureFlag('printsuperadmindiscountenabled'),
  printGalleryThemeDisplayOrder: createRemoteFeatureFlag({ key: 'printgallerythemedisplayorder', supportedValues: ['on', 'off', 'order_a', 'order_b'] }),
  printDigitalInternationalShipping: createRemoteFeatureFlag({ key: 'printdigitalinternationalshipping', supportedValues: ['restricted', 'off'] }),
  printInternationalShipping: createRemoteFeatureFlag({ key: 'printinternationalshipping', supportedValues: ['restricted', 'off'] }),
  printImagineOptions: createRemoteFeatureFlag('printimagineoptions'),
  printImagineHoliday: createRemoteFeatureFlag('printimagineholiday'),
  printImagineStyles: createRemoteFeatureFlag('printimaginestyles'),
  printLocalCurrency: createRemoteFeatureFlag('printlocalcurrency'),
  printValuePropsData: createRemoteFeatureFlag({ key: 'printvaluepropsdata', supportedValues: ['on', 'group1', 'group2'] }),
  printSaleCodeV2: createRemoteFeatureFlag({ key: 'printsalecodev2', supportedValues: ['group1', 'group2', 'group3'] }),
  printGalleryVideoAutoPlay: createRemoteFeatureFlag('printgalleryvideoautoplay'),
  printCouponCodeLaunchV2: createRemoteFeatureFlag('printcouponcodelaunchv2'),
  printPremiumDigitalPricing: createRemoteFeatureFlag('printpremiumdigitalpricing'),
  printRegistryThankYouCrossSellExperiment: createRemoteFeatureFlag({ key: 'printregistrythankyoucrosssellexperiment', supportedValues: ['control', 'treatment'] }),
  printGalleryEnableSearch: createRemoteFeatureFlag('printgalleryenablesearch'),
  printSearch: createRemoteFeatureFlag('printsearch'),
  printSalesAllowedCountryList: createRemoteFeatureFlag({ key: 'printsalesallowedcountrylist', supportedValues: ['on', 'off'] }),
  printGalleryCardTileCarouselContent: createRemoteFeatureFlag('printgallerycardtilecarouselcontent'),
  printPdpPreviewPaneAssetsData: createRemoteFeatureFlag('printpdppreviewpaneassetsdata'),
  printPdpEnableInteractivePrintPreview: createRemoteFeatureFlag('printpdpenableinteractiveprintpreview'),
  printPaperFoilConfig: createRemoteFeatureFlag('printpaperfoilconfig'),
  printOrderTrackingUpdatedLogic: createRemoteFeatureFlag('printordertrackingupdatedlogic'),
  printCollectPhoneNumberOnIntlShippingAddressEnabled: createRemoteFeatureFlag({ key: 'printcollectphonenumberonintlshippingaddressenabled', supportedValues: ['on', 'off'] }),
  accommodationsRemindMeLaterExperiment: createRemoteFeatureFlag({ key: 'accommodationsremindmelaterexperiment', supportedValues: ['control', 'treatment'] }),
  remindMeLaterInGuestGuide: createRemoteFeatureFlag({ key: 'remindmelateringuestguide', supportedValues: ['on', 'off'] }),
  stationeryBusySeasonsShippingWarningsEnabled: createRemoteFeatureFlag('stationerybusyseasonsshippingwarningsenabled'),
  printAutofillPhotos: createRemoteFeatureFlag('printautofillphotos'),
  printPreviewWithPhotoCards: createRemoteFeatureFlag({ key: 'printpreviewwithphotocards', supportedValues: ['on', 'off'], defaultValue: 'off' }),
  printCart: createRemoteFeatureFlag('printcart'),
  /*
   * Website Designer Remote Feature Flags
   */
  websiteDesignerTemplatesGalleryFullWidthEnabled: createRemoteFeatureFlag({ key: 'websitedesignertemplatesgalleryfullwidthenabled', supportedValues: ['on', 'off'] }),
  websiteWelcomePageAsCustomPage: createRemoteFeatureFlag({ key: 'websitewelcomepageascustompage', supportedValues: ['on', 'off'] }),
  websiteDesignerTemplatesGalleryV2Enabled: createRemoteFeatureFlag({ key: 'websitedesignertemplatesgalleryv2enabled', supportedValues: ['on', 'off'] }),
  websiteDesignerFullWebsitePreviewEnabled: createRemoteFeatureFlag({ key: 'websitedesignerfullwebsitepreviewenabled', supportedValues: ['on', 'off'] }),
  websiteDesignerV3Enabled: createRemoteFeatureFlag({ key: 'websitedesignerv3enabled', supportedValues: ['on', 'off'] }),
  websiteHeaderAndNavEffectsEnabled: createRemoteFeatureFlag('websiteheaderandnaveffectsenabled'),
  websiteHeaderPhotoBackgroundAutoContrastEnabled: createRemoteFeatureFlag({ key: 'websiteheaderphotobackgroundautocontrastenabled', supportedValues: ['on', 'off'] }),
  websiteAllPagesAsCustomPagesEnabled: createRemoteFeatureFlag({ key: 'websiteallpagesascustompagesenabled', supportedValues: ['on', 'off', 'onjoysuperuser'] }),
  websitePhotoGalleryComponentLayoutSelectorEnabled: createRemoteFeatureFlag({ key: 'websitephotogallerycomponentlayoutselectorenabled', supportedValues: ['on', 'off'] }),
  sunsetVideoPageEnabled: createRemoteFeatureFlag({ key: 'sunsetvideopageenabled', supportedValues: ['on', 'off'] }),
  websiteDesignerCustomArtEnabled: createRemoteFeatureFlag({ key: 'websitedesignercustomartenabled', supportedValues: ['on', 'off'] }),
  websiteDesignerTemplatePreviewEnabled: createRemoteFeatureFlag({ key: 'websitedesignertemplatepreview', supportedValues: ['on', 'off'] }),
  websiteDesignerRecommendedDesignsFromPhoto: createRemoteFeatureFlag({ key: 'websitedesignerrecommendeddesignsfromphoto', supportedValues: ['on', 'off'] }),
  websiteTemplatesEnableAISearch: createRemoteFeatureFlag('websitetemplatesenableaisearch'),
  websiteGuestNavigationExperiment: createRemoteFeatureFlag({
    key: 'websiteguestnavigationexperiment',
    supportedValues: ['control', 'treatment'],
    defaultValue: 'control',
  }),
  websiteUseNewSaveMutation: createRemoteFeatureFlag('websiteusenewsavemutation'),
  websiteTemplatesEnableHoverAnimation: createRemoteFeatureFlag('websitetemplatesenablehoveranimation'),
  websiteTemplatesEnableThumbnailAutoplay: createRemoteFeatureFlag({ key: 'websitetemplatesenablethumbnailautoplay', supportedValues: ['on', 'off'] }),
  websiteTemplatesEnableDefaultPhotoFallback: createRemoteFeatureFlag({ key: 'websitetemplatesenabledefaultphotofallback', supportedValues: ['on', 'off'] }),
  websiteFullySsrGuestSite: createRemoteFeatureFlag('websitefullyssrguestsite'),
  websiteTemplatesEnableThumbnailCustomization: createRemoteFeatureFlag({ key: 'websitetemplatesenablethumbnailcustomization', supportedValues: ['on', 'off'] }),
  websiteTemplatesLivePreviewEnabled: createRemoteFeatureFlag({ key: 'websitetemplateslivepreviewenabled', supportedValues: ['on', 'off'] }),
  websiteTemplatesGalleryAiSmartCropEnabled: createRemoteFeatureFlag({ key: 'websitetemplatesgalleryaismartcropenabled', supportedValues: ['on', 'off'] }),
  websiteTemplatesGalleryAiDesignRecommendationsEnabled: createRemoteFeatureFlag({ key: 'websitetemplatesgalleryaidesignrecommendationsenabled', supportedValues: ['on', 'off'] }),
  websiteTemplateValuePropsData: createRemoteFeatureFlag({ key: 'websitetemplatevaluepropsdata', supportedValues: ['on'] }),
  unifiedWebsiteEditorExperience: createRemoteFeatureFlag({ key: 'unifiedwebsiteeditorexperience', supportedValues: ['on', 'off'] }),
  /**
   * Drives charm server -- fetches amplitude FF variants to be hydrated during SSR.
   */
  websiteCharmFullySsrGuestSite: createRemoteFeatureFlag('websitecharmfullyssrguestsite'),
  /**
   * PLAN-17: consolidate the guest-site SSR queries. When on, a single
   * GuestSiteEventData query is fired in prefetchData to pre-warm the Apollo cache
   * so the legacy eventByName queries resolve cache-first (one eventByName
   * resolution per SSR instead of four).
   */
  guestSiteConsolidatedSsrQuery: createRemoteFeatureFlag('guestsiteconsolidatedssrquery'),
  autoNavigateSingleComponentEnabled: createRemoteFeatureFlag({ key: 'autonavigatesinglecomponent', supportedValues: ['on', 'off'] }),
  // Per-Guest Page Visibility global master switch / kill switch. Distinct key from the
  // per-event `websitepageperguestvisibility` DB flag (the `enabled` suffix avoids confusing the two).
  // AND-ed with the per-event flag to gate the option, and drives whether new events provision it.
  websitePagePerGuestVisibilityEnabled: createRemoteFeatureFlag({ key: 'websitepageperguestvisibilityenabled', supportedValues: ['on', 'off'] }),

  /*
   * Registry Admin Remote Feature Flags
   */
  messagingEnableHotelBlockReminderNotification: createRemoteFeatureFlag('messagingenablehotelblockremindernotification'),
  messagingEnableHotelBlockReminderSMS: createRemoteFeatureFlag('messagingenablehotelblockremindersms'),
  registryCashFundCurrency18nEnabled: createRemoteFeatureFlag('registryCashFundCurrency18nEnabled'),
  registryDisableSyncRegistry: createRemoteFeatureFlag({ key: 'registryDisableSyncRegistry', supportedValues: ['control', 'treatment'] }),
  registrySyncIsDisabledDate: createRemoteFeatureFlag({ key: 'registrySyncIsDisabledDate' }),
  registryKycLaterEnabled: createRemoteFeatureFlag('registryKycLaterEnabled'),
  registryDashboardTitlesExperiment: createRemoteFeatureFlag({ key: 'registryDashboardTitlesExperiment', supportedValues: ['control', 'treatment'] }),
  registryOverviewVirtualConsultation: createRemoteFeatureFlag({ key: 'registryOverview-VirtualConsultation', supportedValues: ['control', 'treatment'] }),
  //old universal recommendation flag - buggy flag - we keep it here to rise an issue to amplitude
  syncAndTransferRegistryProductRecommendation: createRemoteFeatureFlag({
    key: 'syncAndTransferRegistryProductRecommendation',
    supportedValues: ['control', 'treatmentskipextrastepsintransfersync', 'treatmentfullrecommendationflow'],
  }),
  /*
   * Travel Remote Feature Flags
   */
  hotelBlockEnabled: createRemoteFeatureFlag({ key: 'hotelBlockEnabled', supportedValues: ['on', 'off'] }),
  shortlistReleased: createRemoteFeatureFlag('shortlistreleased'),
  hotelBlockContactMethodMeeting: createRemoteFeatureFlag({ key: 'hotelBlockContactMethodMeeting', supportedValues: ['off', 'on'] }),
  hotelBlockContactMethodSms: createRemoteFeatureFlag({ key: 'hotelBlockContactMethodSms', supportedValues: ['off', 'on'] }),
  hotelBlockContactMethodEmail: createRemoteFeatureFlag({ key: 'hotelBlockContactMethodEmail', supportedValues: ['off', 'on'] }),
  newtimepickerEnabled: createRemoteFeatureFlag({ key: 'newtimepicker', supportedValues: ['on', 'off'] }),
  perkIntercomWorkflowTest: createRemoteFeatureFlag({ key: 'perkIntercomWorkflowTest', supportedValues: ['on', 'off'] }),
  roomBlockManagerDialogPerkPrintPromoCode: createRemoteFeatureFlag({ key: 'roomblockmanagerdialogperkprintpromocode', supportedValues: ['on', 'off'] }),
  roomBlockManagerDialogRegistryPromoCode: createRemoteFeatureFlag({ key: 'roomblockmanagerdialogregistrypromocode', supportedValues: ['on', 'off'] }),
  messagingPostRsvpHotelSearchUrl: createRemoteFeatureFlag({ key: 'messagingpostrsvphotelsearchurl', supportedValues: ['off', 'on'] }),
  postRSVPEnableNearbyHotelsCard: createRemoteFeatureFlag({ key: 'postrsvpenablenearbyhotelscard', supportedValues: ['control', 'treatment'] }),
  postRsvpBookingAutoReminder: createRemoteFeatureFlag({ key: 'postrsvpbookingautoreminder', supportedValues: ['on', 'off'] }),
  venueAddressLocationEnabled: createRemoteFeatureFlag({ key: 'venueAddressLocationEnabled', supportedValues: ['on', 'off'] }),
  hotelRedirectInterceptorEnabled: createRemoteFeatureFlag({ key: 'hotelRedirectInterceptorEnabled', supportedValues: ['on', 'off'] }),
  travelAccommodationConnectionEnabled: createRemoteFeatureFlag({ key: 'travelAccommodationConnectionEnabled', supportedValues: ['on', 'off'] }),
  travelAccommodationsConsoleV2: createRemoteFeatureFlag({ key: 'travelaccommodationsconsolev2', supportedValues: ['control', 'treatment'] }),
  travelCarRentalConsole: createRemoteFeatureFlag({ key: 'travelCarRentalConsole', supportedValues: ['control', 'treatment'] }),
  localeBasedHotelCurrency: createRemoteFeatureFlag({ key: 'localeBasedHotelCurrency', supportedValues: ['on', 'off'] }),
  // Enables page currency setting in accommodations settings
  accommodationsPageCurrencyEnabled: createRemoteFeatureFlag({ key: 'accommodationsPageCurrencyEnabled', supportedValues: ['on', 'off'] }),
  // Enables check-in/check-out date settings in Where to Stay
  enableCheckInCheckOutDateInTravelSettings: createRemoteFeatureFlag({
    key: 'enableCheckInCheckOutDateInTravelSettings',
    supportedValues: ['control', 'treatment'],
  }),
  enableAccommodationsPriceComparisonV2: createRemoteFeatureFlag({ key: 'enableAccommodationsPriceComparisonV2', supportedValues: ['on', 'off'] }),
  accommodationsPriceComparisonExperiment: createRemoteFeatureFlag({ key: 'accommodationsPriceComparisonExperiment', supportedValues: ['link', 'price', 'no-comparison'] }),
  minimumPercentageOffToDisplay: createRemoteFeatureFlag({ key: 'minimumPercentageOffToDisplay', supportedValues: ['on', 'off'] }),
  accommodationPlaceUserNote: createRemoteFeatureFlag({ key: 'accommodationplaceusernote', supportedValues: ['on', 'off'] }),
  accommodationsDiscountTooltipEnabled: createRemoteFeatureFlag({ key: 'accommodationsDiscountTooltipEnabled', supportedValues: ['control', 'treatment'] }),

  /**
   * Mobile listings panel:
   * - control: current mobile layout
   * - treatment_clickable_card: new mobile layout with tap-anywhere card interaction
   * - treatment_cta_button: new mobile layout with CTA button interaction
   */
  accommodationsListingEntryMobileLayoutV2: createRemoteFeatureFlag({
    defaultValue: 'control',
    supportedValues: ['control', 'treatment_clickable_card', 'treatment_cta_button'],
    key: 'accommodationslistingentrymobilelayoutv2',
  }),
  accomodationsNewMobilePDPEnabled: createRemoteFeatureFlag('accommodationsnewmobilepdp'),
  accommodationsPlaceholdersCountOverride: createRemoteFeatureFlag('accommodationsplaceholderscountoverride'),
  hotelpdp3: createBooleanFeatureFlag('hotelpdp3', false),
  accommodationsCustomPageSlugKeywords: createRemoteFeatureFlag('accommodationsCustomPageSlugKeywords'),
  travelStripeExpressPayments: createRemoteFeatureFlag({ key: 'travelStripeExpressPayments', supportedValues: ['control', 'treatment'] }),
  travelStripeExpressPaymentsPDP: createRemoteFeatureFlag({ key: 'travelStripeExpressPaymentsPDP', supportedValues: ['control', 'treatment'] }),
  accommodationLinkInterceptionEnabled: createRemoteFeatureFlag({ key: 'accommodationLinkInterceptionEnabled', supportedValues: ['control', 'treatment'] }),
  accommodationsTravelDistanceExperiment: createRemoteFeatureFlag({ key: 'accommodationsTravelDistanceExperiment', supportedValues: ['control', 'treatment'] }),
  // feature flag with payload of all hotel coupons, used for shared look-up between FE and travel_reservation_service
  travelHotelCoupons: createRemoteFeatureFlag({ key: 'travelHotelCoupons', supportedValues: ['on', 'off'] }),
  placeToStayInCustomPage: createRemoteFeatureFlag({ key: 'placeToStayInCustomPage', supportedValues: ['control', 'treatment'] }),
  stayBookingAssistant: createRemoteFeatureFlag({ key: 'stayBookingAssistant', supportedValues: ['control', 'treatment'] }),
  travelHotelPDPCreatesReservation: createRemoteFeatureFlag({ key: 'travelHotelPDPCreatesReservation', supportedValues: ['on', 'off'] }),
  /** Enables filtering cancellation policy offers above the allowed surcharge threshold. */
  travelHotelOfferSurchargeFilter: createRemoteFeatureFlag({ key: 'travelHotelOfferSurchargeFilter', supportedValues: ['control', 'treatment'] }),
  /** Hides non-refundable rates that are outpriced by a refundable rate for the same board basis. */
  hideExpensiveNonRefundableRates: createRemoteFeatureFlag({ key: 'hideExpensiveNonRefundableRates', supportedValues: ['control', 'treatment'] }),
  pdpCustomerReviews: createRemoteFeatureFlag({ key: 'pdpCustomerReviews', supportedValues: ['control', 'treatment'] }),
  /** For surfacing AI generated properties on auto-populated accommodations (whyThisHotelIsAGreatPick and proposedNoteFromHostToGuest) */
  travelAutoPopulatedAiNotesSurfaced: createRemoteFeatureFlag({ key: 'travelAutoPopulatedAiNotesSurfaced', supportedValues: ['on', 'off'] }),
  /**
   * Controls how hotel offers should be matched when merging cancellation policies.
   * See OfferMatchStrategy enum for available values and their descriptions.
   */
  travelHotelOfferMatchStrategy: createRemoteFeatureFlag({
    key: 'travelHotelOfferMatchStrategy',
    // Had to move this locally because of TSC complaints.
    supportedValues: ['control', 'offergroupid', 'unitname', 'groupidandunitname'],
  }),
  travelDuplicateBookingVerificationGate: createRemoteFeatureFlag({ key: 'travelDuplicateBookingVerificationGate', supportedValues: ['on', 'off'] }),
  venueLocationSyncToggle: createRemoteFeatureFlag({ key: 'venuelocationsynctoggle', supportedValues: ['on', 'off'] }),
  travelCheckoutEnableCached: createRemoteFeatureFlag({ key: 'travelCheckoutEnableCached', supportedValues: ['control', 'treatment', 'on'] }),
  /** Enables offer token refresh on checkout page when offer is expired/invalid */
  travelCheckoutRefreshOfferToken: createRemoteFeatureFlag({ key: 'travelCheckoutRefreshOfferToken', supportedValues: ['control', 'treatment'] }),
  /** Detects and shows price/policy changes from prebook on checkout landing (TRAV-3481) */
  travelCheckoutPriceChangeDetection: createRemoteFeatureFlag({ key: 'travelCheckoutPriceChangeDetection', supportedValues: ['control', 'treatment'] }),
  /** A/B experiment: Joy discount coupon field on travel checkout (TRAV-3420) */
  travelCheckoutJoyCoupon: createRemoteFeatureFlag({ key: 'travelCheckoutJoyCoupon', supportedValues: ['control', 'treatment'] }),
  /** Experiment flag for the cash-fund hotel-search variant (TRAV-3348) */
  hotelsSearchCashFundExperiment: createRemoteFeatureFlag({ key: 'hotelsSearchCashFundExperiment', supportedValues: ['control', 'treatment'] }),
  /** A/B test: cash-fund checkout UI (fund vs. card CTA) for logged-in couples with enough fund. See TRAV-3349 */
  travelCheckoutCashFundExperiment: createRemoteFeatureFlag({ key: 'travelCheckoutCashFundExperiment', supportedValues: ['control', 'treatment'] }),
  /** Enables AI recommendations note to show on tour step else show tags. See TRAV-1859 */
  aiRecommendationsNoteEnabled: createRemoteFeatureFlag({ key: 'aiRecommendationsNoteEnabled', supportedValues: ['on', 'off'] }),
  /** A/B test: Hide visibility toggle on accommodations page */
  accommodationsHideVisibilityToggle: createRemoteFeatureFlag({ key: 'accommodationsHideVisibilityToggle', supportedValues: ['control', 'treatment'] }),
  /** Rollout: replace GoogleMapEmbed iframe with StaticMap image in schedule/travel MapBlock components */
  guestStaticMapRollout: createRemoteFeatureFlag({ key: 'guestStaticMapRollout', supportedValues: ['on', 'off'] }),
  /** Experiment A: Promotes hotel deals with countdown subtitle in guest guide */
  guestGuideHotelDealsPromotionExperiment: createRemoteFeatureFlag({ key: 'guestGuideHotelDealsPromotionExperiment', supportedValues: ['control', 'treatment'] }),
  /** Experiment B: Shows prominent booking CTA card at top of guest guide */
  guestGuideBookingCtaExperiment: createRemoteFeatureFlag({ key: 'guestGuideBookingCtaExperiment', supportedValues: ['control', 'treatment'] }),
  /** Enables section-level layout selector in accommodations admin */
  accommodationsLayoutSelector: createRemoteFeatureFlag({ key: 'accommodationsLayoutSelector', supportedValues: ['control', 'treatment'] }),
  /** Compact 2-column card layout for hotel PDP on mobile — treatment shows compact cards with drawer detail view */
  hotelPDPCompactCards: createRemoteFeatureFlag({ key: 'hotelPDPCompactCards', supportedValues: ['control', 'treatment'] }),
  /** Shows offers from the alternate provider (zh↔nuitee) in a collapsible "More Offers" section on the hotel PDP */
  enableAlternateProviderOffers: createRemoteFeatureFlag({ key: 'enableAlternateProviderOffers', supportedValues: ['control', 'treatment'] }),
  /** Promotes alternate-provider offers into the main grid so at least `payload.minOfferCount` offers are visible before "Show More" */
  alternateProviderMinimumOffers: createRemoteFeatureFlag({ key: 'alternateproviderminimumoffers', supportedValues: ['control', 'treatment'] }),
  /*
   * Contact Collection Feature Flags
   */
  contactCollectionSmsAgreementVariant: createRemoteFeatureFlag({ key: 'contactCollectionSmsAgreementVariant', supportedValues: ['variant1', 'variant2'] }),
  enableContactCollectionMessage: createRemoteFeatureFlag({ key: 'enablecontactcollectionmessage', supportedValues: ['on', 'off'] }),
  contactCollectionDisablePhoneNumberValidationFor: createRemoteFeatureFlag('contactcollectiondisablephonenumbervalidationfor'),
  /** Restructures Contact Collector admin into a Send / Activity / Settings tabnav and unifies the experience across `/contact` and `/collect-contacts` */
  contactCollectorTabNav: createRemoteFeatureFlag({ key: 'contactCollectorTabNav', supportedValues: ['on', 'off'] }),
  /** Per-collector REQUIRED/OPTIONAL/OFF settings for email/phone/address. Gates admin UI and guest field-rule reading. */
  contactCollectorFieldSettingsEnabled: createRemoteFeatureFlag({ key: 'contactCollectorFieldSettingsEnabled', supportedValues: ['on', 'off'] }),
  shopSubCatHighlightsExperiment: createRemoteFeatureFlag({ key: 'shopSubCatHighlightsExperiment', supportedValues: ['control', 'treatment'] }),

  weddingShopFeatureEnabled: createRemoteFeatureFlag({ key: 'weddingshopfeatureenabled', supportedValues: ['on', 'off', 'variant2'] }),
  /**
   * Admin Dashboard
   */
  photoAlbumMultiPhotoUploadEnabledV2: createRemoteFeatureFlag({ key: 'photoalbummultiphotouploadv2', supportedValues: ['on', 'off'] }),
  albumSlideShowExperiment: createRemoteFeatureFlag({ key: 'albumSlideShowExperiment', supportedValues: ['control', 'treatment'] }),
  allCollectionPhotosEnabled: createRemoteFeatureFlag({ key: 'allcollectionphotosenabled', supportedValues: ['on', 'off'] }),
  photoUploadFromCollectionEnabled: createRemoteFeatureFlag({ key: 'photouploadfromcollection', supportedValues: ['on', 'off'] }),
  photoGalleryCustomBlockEnabled: createRemoteFeatureFlag({ key: 'photogallerycustomblockenabled', supportedValues: ['on', 'off'] }),
  websiteThingsToDoAnnouncement: createRemoteFeatureFlag({ key: 'websiteThingsToDoAnnouncement', supportedValues: ['on', 'off'] }),
  eventPhotoV2UrlMigration: createRemoteFeatureFlag({ key: 'eventphotov2urlmigration', supportedValues: ['on', 'off'] }),
  websitePhotoSortingEnabled: createRemoteFeatureFlag({ key: 'websitephotosortingenabled', supportedValues: ['on', 'off'] }),
  websitePhotoAssignToPageEnabled: createRemoteFeatureFlag('websitephotosassigntopageenabled'),
  websiteReplaceDefaultPhotosEnabled: createRemoteFeatureFlag('websitereplacedefaultphotosenabled'),
  websiteUploadPhotosFromMobileEnabled: createRemoteFeatureFlag('websiteuploadphotosfrommobileenabled'),
  websitePhotosCreatePageFromAlbumEnabled: createRemoteFeatureFlag('websitephotoscreatepagefromalbumenabled'),
  websitePhotosEngagementAlbumEnable: createRemoteFeatureFlag('websitephotosengagementalbumenable'),
  printStudioOnDashboard: createRemoteFeatureFlag({ key: 'printstudioondashboard', supportedValues: ['on', 'off'] }),

  /**
   * Guest site
   */
  hideJoyLogo: createRemoteFeatureFlag('hidejoylogo'),
  guestBrannanCoverPhotoV2Enabled: createRemoteFeatureFlag({ key: 'guestbrannancoverphotov2enabled', supportedValues: ['on', 'off'] }),
  websiteVideoBackgroundEnabled: createRemoteFeatureFlag({ key: 'websiteVideoBackgroundEnabled', supportedValues: ['on', 'off'] }),
  websiteMonogramBackgroundEnabled: createRemoteFeatureFlag({ key: 'websiteMonogramBackgroundEnabled', supportedValues: ['on', 'off'] }),

  // TODO: if we are resolving hideGetTheAppPageExperiment flag, we need to cleanup some "Get the App" code for that please refer - https://github.com/joylifeinc/joy-web/pull/9648
  hideGetTheAppPageExperiment: createRemoteFeatureFlag({ key: 'hidegettheapppageexperiment', supportedValues: ['control', 'treatment'] }),
  websiteInterceptExternalHotelLinksV2: createRemoteFeatureFlag({ key: 'websiteinterceptexternalhotellinksv2', supportedValues: ['on', 'off'] }),
  websiteInterceptExternalHotelLinksAdmin: createRemoteFeatureFlag({ key: 'websiteinterceptexternalhotellinksadmin', supportedValues: ['on', 'off'] }),
  guestguideexperiment: createRemoteFeatureFlag({
    key: 'guestguideexperiment',
    supportedValues: ['control', 'treatment'],
    consentValueForTrue: 'treatment',
  }),
  showGuestGuide: createFeatureFlagConfig('showGuestGuide', true),
  accommodationsPriceAlerts: createRemoteFeatureFlag({ key: 'accommodationsPriceAlerts', supportedValues: ['control', 'treatment'] }),
  accommodationsPageCountdown: createRemoteFeatureFlag({ key: 'accommodationsPageCountdown', supportedValues: ['control', 'treatment'] }),
  guestSkipPagePhotoRendition: createRemoteFeatureFlag({ key: 'guestskippagephotorendition', supportedValues: ['on', 'off'] }),
  guestPageContentRedesign: createRemoteFeatureFlag({ key: 'guestPageContentRedesign', supportedValues: ['on', 'off'] }),
  websiteSchedulePageLayoutTypeSelectorEnabled: createRemoteFeatureFlag('websiteschedulepagelayouttypeselectorenabled'),
  guestBackgroundHeaderBouncingChevronEnabled: createRemoteFeatureFlag({ key: 'guestbackgroundheaderbouncingchevron', supportedValues: ['on', 'off'] }),
  guestMobileFooterNavEnabled: createRemoteFeatureFlag({ key: 'guestmobilefooternav', supportedValues: ['on', 'off'] }),
  guestResetLetterSpacingForFontPack: createRemoteFeatureFlag({ key: 'guestresetletterspacingforfontpack', supportedValues: ['on', 'off'] }),
  websiteSelfHostedGoogleFontsEnabled: createRemoteFeatureFlag({ key: 'websiteselfhostedgooglefontsenabled', supportedValues: ['on', 'off'] }),
  websiteInitialHeaderAnimationDisabled: createRemoteFeatureFlag({ key: 'websiteinitialheaderanimationdisabled', supportedValues: ['on', 'off'] }),

  /**
   * Webiste Design
   */
  joyWebAppBoundaryEnabled: createRemoteFeatureFlag({ key: 'joywebappboundaryenabled', supportedValues: ['on', 'off'] }),
  previewSiteWithThemeExperiment: createRemoteFeatureFlag({ key: 'marcomViewDemoSiteExperiment', supportedValues: ['control', 'treatment'] }),
  websiteTemplateOrdering: createRemoteFeatureFlag({ key: 'websitetemplateordering', supportedValues: ['on', 'off', 'treatment'] }),
  hotelFirstPartySearchEnabled: createRemoteFeatureFlag({ key: 'hotelfirstpartysearchenabled', supportedValues: ['control', 'treatment'] }),
  adminDashboardHoneymoonEnabled: createRemoteFeatureFlag({ key: 'adminDashboardHoneymoonEnabled', supportedValues: ['on', 'off'] }),
  // Honeymoon Painted Door experiment — master kill switch; payload carries { maxEventAgeHours }.
  honeymoonPaintedDoorEnabled: createRemoteFeatureFlag({ key: 'honeymoonPaintedDoorEnabled', supportedValues: ['on', 'off'] }),
  // Honeymoon Painted Door experiment — per-version experiment (US-targeted). One per version (V1, V2, …).
  honeymoonPaintedDoorV1: createRemoteFeatureFlag({ key: 'honeymoonPaintedDoorV1', supportedValues: ['on', 'off'] }),
  honeymoonPaintedDoorV2: createRemoteFeatureFlag({ key: 'honeymoonPaintedDoorV2', supportedValues: ['on', 'off'] }),
  // Honeymoon Painted Door experiment — shared "seen" consent key (also a UserConsentKey); required so
  // useIsFeatureEnabled({ consentKey: 'honeymoonPaintedDoorSeen' }) resolves to a valid ConsentFeatureFlagKey.
  honeymoonPaintedDoorSeen: createRemoteFeatureFlag({ key: 'honeymoonPaintedDoorSeen', supportedValues: ['on', 'off'] }),
  accommodationsGuestHotelTileExperiment: createRemoteFeatureFlag({ key: 'accommodationsGuestHotelTileExperiment', supportedValues: ['control', 'treatment'] }),
  /** Experiment flag: sold-out hotels open their Joy PDP ("View") with an "Explore Hotels Nearby" secondary CTA */
  accommodationsSoldOutPdpExperiment: createRemoteFeatureFlag({ key: 'accommodationsSoldOutPdpExperiment', supportedValues: ['control', 'treatment'] }),
  /** Experiment flag to test a "Guest Favorite" / "Our Pick" pill on host-favorited accommodations */
  accommodationsFavoriteTagExperiment: createRemoteFeatureFlag({
    key: 'accommodationsFavoriteTagExperiment',
    supportedValues: ['control', 'treatment_guest_favorite', 'treatment_our_pick'],
  }),
  /** Experiment flag to test different versions of the accommodation visibility confirmation modal */
  accommodationVisibilityConfirmationModalExperiment: createRemoteFeatureFlag({
    key: 'accommodationvisibilityconfirmationmodalexperiment',
    supportedValues: ['0', '1', '2', '3'],
    defaultValue: '0',
  }),
  bookingCountExperiment: createRemoteFeatureFlag({ key: 'bookingCountExperiment', supportedValues: ['control', 'treatment'] }),
  travelGuestCurrencySwitch: createRemoteFeatureFlag({ key: 'travelGuestCurrencySwitch', supportedValues: ['on', 'off'] }),
  /** Controls which currency codes are available in the travel currency selector via payload */
  travelCurrencies: createRemoteFeatureFlag({ key: 'travelCurrencies', supportedValues: ['control', 'treatment'] }),
  /**
   * Create page - Native specs
   */
  createPageNativeSpecs: createRemoteFeatureFlag({ key: 'createPageNativeSpecs', supportedValues: ['control', 'treatment'] }),
  /**
   * Dashboard Travel Survey
   */
  dashboardTravelSurvey: createRemoteFeatureFlag({ key: 'dashboardTravelSurvey', supportedValues: ['on', 'off'] }),
  /**
   * Honeymoon lead gen Survey
   */
  honeymoonLeadGenSurvey: createRemoteFeatureFlag({ key: 'honeymoonLeadGenSurvey', supportedValues: ['on', 'off'] }),
  /**
   * Day-Of Coordinator Survey
   */
  dayOfLeadGenSurvey: createRemoteFeatureFlag({ key: 'dayOfLeadGenSurvey', supportedValues: ['on', 'off'] }),
  /**
   * Cancellation Reason Survey
   */
  cancellationReasonSurvey: createRemoteFeatureFlag({ key: 'cancelationreasonsurvey', supportedValues: ['on', 'off'] }),

  /**
   * CUG Not Verified Page
   */
  cugAccessCodes: createRemoteFeatureFlag({ key: 'cugAccessCodes', supportedValues: ['on', 'off'] }),
  /**
   * General Event
   */
  enableCreateGeneralEventRoutes: createRemoteFeatureFlag({ key: 'enablecreategeneraleventroutes', supportedValues: ['on', 'off'] }),
  generalEventSettingsUsersV2EnableAddCollaboratorsButton: createRemoteFeatureFlag({
    key: 'generaleventsettingsusersv2enableaddcollaboratorsbutton',
    supportedValues: ['on', 'off'],
  }),

  /**
   * Wedding Event Creation
   */
  createWeddingDefaultThemeExperiment: createRemoteFeatureFlag({ key: 'createWeddingDefaultThemeExperiment', supportedValues: ['control', 'treatment1', 'treatment2'] }),
  createWeddingHeaderLayoutExperiment: createRemoteFeatureFlag({ key: 'createWeddingHeaderLayoutExperiment', supportedValues: ['on', 'off'] }),
  createWeddingApplyMonogramInitials: createRemoteFeatureFlag({ key: 'createWeddingApplyMonogramInitials', supportedValues: ['on', 'off'] }),
  /**
   * i18n
   */
  joyWebTranslationsEnabled: createRemoteFeatureFlag({ key: 'joywebtranslations', supportedValues: ['on', 'off'] }),
  /*
   * General event and joy pro feature flags
   */
  generalEventCategoryConfig: createRemoteFeatureFlag({ key: 'generaleventcategoryconfig', supportedValues: ['on', 'off'] }),
  /**
   * Sanitize string
   */
  nuiteeQueryStringSanitization: createRemoteFeatureFlag({ key: 'nuiteeQueryStringSanitization', supportedValues: ['on', 'off'] }),
  /*
   * User consent feature flags
   */
  dayOfLeadGenSurveyCompleted: createRemoteFeatureFlag({ key: 'dayOfLeadGenSurveyCompleted', supportedValues: ['on', 'off'] }),
  enablePostRsvpHotelBookReminder: createRemoteFeatureFlag({ key: 'enablePostRsvpHotelBookReminder', supportedValues: ['on', 'off'] }),
  expandedMapAnnouncementSeen: createRemoteFeatureFlag({ key: 'expandedMapAnnouncementSeen', supportedValues: ['on', 'off'] }),
  honeymoonAnnouncementSeen: createRemoteFeatureFlag({ key: 'honeymoonAnnouncementSeen', supportedValues: ['on', 'off'] }),
  honeymoonLeadGenSurveyCompleted: createRemoteFeatureFlag({ key: 'honeymoonLeadGenSurveyCompleted', supportedValues: ['on', 'off'] }),
  isAutoPopulationDone: createRemoteFeatureFlag({ key: 'isAutoPopulationDone', supportedValues: ['on', 'off'] }),
  scheduleOptOutAddToCalendar: createRemoteFeatureFlag({ key: 'scheduleOptOutAddToCalendar', supportedValues: ['on', 'off'] }),
  scheduleOptOutDirections: createRemoteFeatureFlag({ key: 'scheduleOptOutDirections', supportedValues: ['on', 'off'] }),
  scheduleOptOutWhereToStayLink: createRemoteFeatureFlag({ key: 'scheduleOptOutWhereToStayLink', supportedValues: ['on', 'off'] }),
  showAddToCalendarPerScheduleEventEnabled: createRemoteFeatureFlag({
    key: 'showAddToCalendarPerScheduleEventEnabled',
    supportedValues: ['on', 'off'],
  }),
  showDirectionsPerScheduleEventEnabled: createRemoteFeatureFlag({
    key: 'showDirectionsPerScheduleEventEnabled',
    supportedValues: ['on', 'off'],
  }),
  newScheduleEditorForOldEventsEnabled: createRemoteFeatureFlag({ key: 'newScheduleEditorForOldEventsEnabled', supportedValues: ['on', 'off'] }),
  showAiRecommendationsFeedbackDialog: createRemoteFeatureFlag({ key: 'showAiRecommendationsFeedbackDialog', supportedValues: ['on', 'off'] }),
  showAutoPopulationCompletedDialog: createRemoteFeatureFlag({ key: 'showAutoPopulationCompletedDialog', supportedValues: ['on', 'off'] }),
  showGuestsNearbyHotels: createRemoteFeatureFlag({ key: 'showGuestsNearbyHotels', supportedValues: ['on', 'off'] }),
  // Default on so guests see booking links unless couples opt out.
  showBookingLinksOnRegistryPurchaseConfirmation: createRemoteFeatureFlag({
    key: 'showBookingLinksOnRegistryPurchaseConfirmation',
    supportedValues: ['on', 'off'],
    defaultValue: 'on',
  }),
  showExploreMoreStaysOnGuestSite: createRemoteFeatureFlag({ key: 'showExploreMoreStaysOnGuestSite', supportedValues: ['on', 'off'], defaultValue: 'on' }),
  showMothersDayDashboardModal: createRemoteFeatureFlag({ key: 'showMothersDayDashboardModal', supportedValues: ['on', 'off'] }),
  showStayAreasOnGuestSite: createRemoteFeatureFlag({ key: 'showStayAreasOnGuestSite', supportedValues: ['on', 'off'], defaultValue: 'on' }),
  showValentinesDayDialog: createRemoteFeatureFlag({ key: 'showValentinesDayDialog', supportedValues: ['on', 'off'] }),
  thingsToDoAnnouncementSeen: createRemoteFeatureFlag({ key: 'thingsToDoAnnouncementSeen', supportedValues: ['on', 'off'] }),
  websiteAllowHotelLinkIntercept: createRemoteFeatureFlag({ key: 'websiteAllowHotelLinkIntercept', supportedValues: ['on', 'off'], defaultValue: 'on' }),
  websiteOptOutTripProfit: createRemoteFeatureFlag({ key: 'websiteOptOutTripProfit', supportedValues: ['on', 'off'] }),
  /*
   * Admin RSVP feature flags
   */
  adminRSVPV2Enabled: createRemoteFeatureFlag({ key: 'adminRSVPV2Enabled', supportedValues: ['on', 'off'] }),
  // Kill switch for the redesigned ("beta") RSVP Responses page. Defaults to 'on' (100% rollout) —
  // when on, admins land on the redesigned page by default and can opt back into the classic page;
  // flip to 'off' in Amplitude to force everyone onto the classic page and hide the switch/badge.
  // See useRsvpResponsesVersion.
  rsvpResponsesBetaEnabled: createRemoteFeatureFlag({ key: 'rsvpResponsesBetaEnabled', supportedValues: ['on', 'off'], defaultValue: 'on' }),
  /*
   * Event Session Optimization
   */
  skipEventSessionForReadOnlyQueries: createRemoteFeatureFlag({ key: 'skipEventSessionForReadOnlyQueries', supportedValues: ['on', 'off'] }),
  /*
   * Guided Moments
   */
  joyGuidedMoments: createRemoteFeatureFlag({ key: 'joyGuidedMoments', supportedValues: ['on', 'off'] }),
  /*
   * Joy Pro
   */
  enableRegistrationApproval: createRemoteFeatureFlag({ key: 'enableregistrationapproval', supportedValues: ['on', 'off'] }),
  carbonTicketingEnabled: createRemoteFeatureFlag({ key: 'carbonticketingenabled', supportedValues: ['on', 'off'] }),
  carbonEnableTicketPayments: createRemoteFeatureFlag({ key: 'carbonEnableTicketPayments', supportedValues: ['on', 'off'] }),
  carbonEnablePromoCodes: createRemoteFeatureFlag({ key: 'carbonEnablePromoCodes', supportedValues: ['on', 'off'] }),
  joyProExposeCarbon: createRemoteFeatureFlag({ key: 'joyproexposecarbon', supportedValues: ['on', 'off'] }),
  joyProRegistrationV2Enabled: createRemoteFeatureFlag({ key: 'joyproregistrationv2enabled', supportedValues: ['on', 'off'] }),
  joyProRegistrationV2EnableTagging: createRemoteFeatureFlag({ key: 'joyproregistrationv2enabletagging', supportedValues: ['on', 'off'] }),
  joyProGuestIdentificationDialog: createRemoteFeatureFlag({ key: 'joyproguestidentificationdialog', supportedValues: ['on', 'off'] }),
  /*
   * IntlTelInput new version
   */
  intlTelInputEnabled: createRemoteFeatureFlag({ key: 'useIntlTelInputEnabled', supportedValues: ['on', 'off'] }),
  /**
   * Website Glass Effect
   */
  websiteGlassEffect: createRemoteFeatureFlag({ key: 'websiteGlassEffect', supportedValues: ['on', 'off'] }),
  /**
   * Language Settings
   */
  languageSettingEnabled: createRemoteFeatureFlag({ key: 'languagesettingenabled', supportedValues: ['on', 'off'] }),
  /**
   * Wedding Party List Circle Avatars
   */
  weddingPartyListCircleAvatars: createRemoteFeatureFlag({ key: 'weddingPartyListCircleAvatars', supportedValues: ['on', 'off'] }),
  /**
   * Exclude custom (API-created) VIP members from the legacy wedding party page (PRNT-4424).
   * When on, the legacy VIP page queries request Firebase-sourced VIPs only.
   */
  excludeCustomVipsFromLegacyPage: createRemoteFeatureFlag({ key: 'excludecustomvipsfromlegacypage', supportedValues: ['on', 'off'] }),
  /**
   * Guest List V2
   */
  guestListV2Enabled: createRemoteFeatureFlag({ key: 'guestListV2Enabled', supportedValues: ['on', 'off'] }),
  useDirectGuestListV2: createRemoteFeatureFlag({ key: 'useDirectGuestListV2', supportedValues: ['on', 'off'] }),

  /**
   * Custom Page ItemList Integration
   * Enables the new ItemList-based component list in custom page editor.
   * @see doc/plans/item-list-integration/implementation-plan.md
   */
  customPageItemListEnabled: createRemoteFeatureFlag({ key: 'customPageItemListEnabled', supportedValues: ['on', 'off'] }),

  /**
   * Custom Components ItemList Unification
   * Gates the migration of FAQ and VIP default-view editors from bespoke
   * SortableList to the shared Joykit ItemList pattern.
   */
  customComponentItemListUnified: createRemoteFeatureFlag({ key: 'customComponentItemListUnified', supportedValues: ['on', 'off'] }),

  /**
   * Multi-day event date (PRNT-4321)
   * Gates the "Date range…" option in the EventDetails block's date-format selector and the
   * range/custom-text rendering on guest surfaces. Amplitude key is lowercase per convention.
   */
  websiteMultiDayEventDateEnabled: createRemoteFeatureFlag({ key: 'websitemultidayeventdateenabled', supportedValues: ['on', 'off'] }),

  /**
   * Travel Admin ItemList Unification (PRNT-4216)
   * Gates the migration of the Travel admin list from bespoke SortableList
   * to the shared Joykit ItemList pattern, matching FAQ and VIP.
   */
  travelAdminItemListUnified: createRemoteFeatureFlag({ key: 'travelAdminItemListUnified', supportedValues: ['on', 'off'] }),

  /**
   * Admin Command Palette (Cmd+K)
   * Gates the command palette on the admin dashboard.
   */
  adminCommandPaletteEnabled: createRemoteFeatureFlag({ key: 'adminCommandPaletteEnabled', supportedValues: ['on', 'off'], defaultValue: 'off' }),

  /**
   * Group Messaging — SMS Channel
   * Gates whether SMS is available as a delivery channel (geo-targeting via Amplitude).
   * Separate from the `useGroupMessaging` event flag which is the feature kill switch.
   */
  smsChannelEnabled: createRemoteFeatureFlag({ key: 'smschannelenabled', supportedValues: ['on', 'off'] }),

  /**
   * Group Messaging — MMS (image-on-SMS)
   * Gates whether hosts can attach an image to SMS. Evaluated only after SMS is enabled
   * (see `useIsMmsEnabled`); the per-event `enableMmsOverride` flag supersedes this value.
   */
  allowMms: createRemoteFeatureFlag({ key: 'allowmms', supportedValues: ['on', 'off'] }),

  rsvpSettingsInJoySettings: createRemoteFeatureFlag('rsvpsettingsinjoysettings'),

  /**
   * Registration Matching Option
   * Enables the option to match the registration form to the design of the website.
   */
  registrationFormMatchingWebsite: createBooleanFeatureFlag('registrationFormMatchingWebsite'),

  /**
   * Gates the honeymoon cash fund success modal that shows after creating a cash fund.
   * When enabled, shows a honeymoon-themed success screen with destination browsing CTA.
   */
  enableHoneymoonCashFundSuccessModal: createRemoteFeatureFlag('enablehoneymooncashfundsuccessmodal'),

  /**
   * C14-278: kill switch for the auth-domain rollout. Variants carry a `payload.domain`
   * which is consumed by `useResolveAuthClientDomain` (see `useAuthClient.ts`).
   * When the flag is missing or returns no payload, the hook falls back to the env-correct
   * proxy domain via `getAuthDomain(joyEnvironment)`.
   *
   * Amplitude: prod project 142907, dev project 142658.
   */
  authDomainClients: createRemoteFeatureFlag('auth-domain-clients'),

  registryInstantPayoutEnabled: createRemoteFeatureFlag('registryInstantPayoutEnabled'),

  /**
   * Gates the visual style showcase on public stationery gallery pages
   * (save-the-date and invitation). Shows style category thumbnails
   * (Botanical, Floral, etc.) that filter the gallery.
   */
  printStyleShowcaseEnabled: createRemoteFeatureFlag({ key: 'printstyleshowcaseenabled', supportedValues: ['on', 'off'] }),
} as const;

export type FeatureFlags = typeof FEATURE_FLAG_VALUES;
